import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { MapComponent } from '@connect-services/client/components/map/map.component';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { FieldInterventionSelectionModalService } from '@connect-services/client/services/field-intervention-selection-modal.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { Layer } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule, SelectComponent],
    selector: 'app-layer-select',
    styleUrl: './layer-select.component.scss',
    templateUrl: './layer-select.component.html',
})
export class LayerSelectComponent implements OnInit {
    public readonly hasTabularLayers = input<boolean>(true);
    public layers: Layer[] = [];
    public readonly options = signal<SelectOption<string[]>[]>([]);

    protected readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    protected readonly layerListBloc = inject(LayerListBloc);

    private readonly fieldInterventionSelectionModalService = inject(FieldInterventionSelectionModalService);
    private readonly layerService = inject(LayerService);
    private readonly mapSourceService = inject(MapSourceService);

    public async changeLayer(layerIds: string[][]): Promise<void> {
        const layers = this.layers.filter((layer) => {
            return layerIds.flatMap((layerId) => layerId).includes(layer.id);
        });
        this.createFieldInterventionService.isLoading.set(true);
        this.createFieldInterventionService.entities.set([]);
        this.createFieldInterventionService.order.set(undefined);
        this.createFieldInterventionService.orderBy.set('');
        this.createFieldInterventionService.isZoneDelimiterFilterActive.set(false);
        this.createFieldInterventionService.zoneDelimiter.set(undefined);

        if (!layers.length) {
            this.createFieldInterventionService.isLoading.set(false);

            return;
        }

        await this.createFieldInterventionService.changeActiveLayer(layers);

        this.mapSourceService.clearSources(
            MapComponent.backgroundSource(),
            this.createFieldInterventionService.currentSources(),
        );

        if (!this.createFieldInterventionService.currentSources().length) {
            this.createFieldInterventionService.isLoading.set(false);

            return;
        }

        this.createFieldInterventionService.showFieldInterventionAreaMask();

        await this.mapSourceService.drawSources(this.createFieldInterventionService.currentSources(), {
            fitBounds: true,
            intersectsFieldInterventionArea: this.createFieldInterventionService.fieldInterventionId(),
        });
        this.fieldInterventionSelectionModalService.openSelectionModal();
    }

    public getDefaultValue(): SelectOption<string[]>[] {
        const activeLayers = this.createFieldInterventionService.activeLayers();

        return activeLayers.length
            ? [
                  {
                      label: activeLayers[0].name,
                      value: activeLayers.map((activeLayer) => activeLayer.id),
                  },
              ]
            : [];
    }

    public async ngOnInit(): Promise<void> {
        const allLayers = await this.layerService.getAllLayers();
        this.layers = allLayers.items
            .filter((layer: Layer) => layer.type === Layer.TypeEnum.TABLE)
            .sort((a: Layer, b: Layer) => a.name.localeCompare(b.name));

        this.createFieldInterventionService.initSelectionObjects(this.layers);

        const groupedLayer = this.layerService.getGroupedLayers(this.layers, this.hasTabularLayers());

        this.createFieldInterventionService.groupedLayer.set(Object.values(groupedLayer));

        this.options.set(
            Object.entries(groupedLayer).map(([layerName, layerIds]) => {
                return { label: layerName, value: layerIds };
            }),
        );
    }
}
